import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils';

const skeletonVariants = cva('animate-pulse bg-gray-200 rounded-md', {
  variants: {
    variant: {
      default: 'bg-gray-200',
      light: 'bg-gray-100',
      dark: 'bg-gray-300',
      primary: 'bg-blue-chaos-light/20',
      secondary: 'bg-magical-moonlight-l/50',
    },
    size: {
      xs: 'h-3',
      sm: 'h-4',
      md: 'h-6',
      lg: 'h-8',
      xl: 'h-12',
      '2xl': 'h-16',
    },
    shape: {
      rectangle: 'rounded-md',
      circle: 'rounded-full',
      pill: 'rounded-full',
      square: 'rounded-md aspect-square',
    },
    width: {
      full: 'w-full',
      auto: 'w-auto',
      xs: 'w-12',
      sm: 'w-16',
      md: 'w-24',
      lg: 'w-32',
      xl: 'w-48',
      '2xl': 'w-64',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    shape: 'rectangle',
    width: 'full',
  },
});

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'light' | 'dark' | 'primary' | 'secondary';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  shape?: 'rectangle' | 'circle' | 'pill' | 'square';
  width?: 'full' | 'auto' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  lines?: number;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant, size, shape, width, lines = 1, ...props }, ref) => {
    if (lines > 1) {
      return (
        <div className="space-y-2" {...props}>
          {Array.from({ length: lines }).map((_, index) => (
            <div
              key={index}
              className={cn(
                skeletonVariants({
                  variant,
                  size,
                  shape,
                  width: index === lines - 1 ? 'md' : width, // Last line is shorter
                }),
                className
              )}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          skeletonVariants({ variant, size, shape, width }),
          className
        )}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export default Skeleton;
export { skeletonVariants };
